<script lang="ts" setup>
import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, provide, reactive, ref, toRaw, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { DeviceError, DeviceService, DummyDeviceService, RealDeviceService, PendingDeviceService } from "@/services/BackendService"
import {replayAfterChange, getActionInputType, tagExists, END_ACTION_ID, actionVisibleInBranch} from "@/services/actionUtils"
import {
    createAction,
    deleteAction,
    getFailedActions,
    getRecording,
    saveAction,
    addComplaint,
    splitTestCase as sendSplitTestCaseRequest,
    inlineTestCase as sendInlineTestCaseRequest,
    getDeviceVariables
} from "@/services/ProjectService"
import { TestcaseService } from "@/services/TestcaseService"
import VActionButton from "@/common/components/form/VActionButton.vue"
import RunHeader from "@/modules/run/components/RunHeader.vue"
import ChatbotPanel from "@/modules/run/components/ChatbotPanel.vue"
import { useStore } from "@/store/useStore"
import { useUndoRedoStore } from "@/store/useUndoRedoStore"
import type { UndoEntry } from "@/store/useUndoRedoStore"
import { useAutoDisconnectStore } from "@/store/useAutoDisconnectStore"
import { blockScreen } from "@/common/util"
import RunVideoDisplay from "@/modules/run/components/RunVideoDisplay.vue"
import RunDeviceScreen from "@/modules/run/components/RunDeviceScreen.vue"
import VEditAndView from "@/common/components/layout/VEditAndView.vue"
import ActionListMain from "@/common/components/action/ActionListMain.vue"
import ActionEdit from "@/common/components/action/ActionEdit.vue"
import { VueCookieNext } from 'vue-cookie-next'
import { ActionDTO } from "@/types/gen"
import type { ActionInput, ProjectDTO, TestCaseDTO } from "@/types/gen"

import bingUrl from '@/assets/bing2.mp3';

const aiRecorderEnabled = window.AI_RECORDER_ENABLED === true

interface Props {
}

const props = defineProps<Props>()

const route = useRoute()
const router = useRouter()

const device = ref<DeviceService>(new PendingDeviceService())
const program = ref({
    mode: "play",
    branch: "",
    actionsLoaded: false
})
provide('mode', program.value.mode)
provide('branch', computed(() => program.value.branch))
const testCaseId = ref<number | null>(null)
const testCaseService = ref(new TestcaseService())
const project = ref<ProjectDTO>()
const justInserted = ref<any>(null)
const openGettingStarted = ref(false)
const sidePanel = ref(true)
const failedActions = ref<any>([])
const prevInsert = ref("")
const vEditAndView = ref<typeof VEditAndView>()
const viewVariables = ref<any>(null) // todo rename to deviceVariables
const cutClipboards = ref<any>([])
const actionSnapshots = new Map<number, ActionDTO>()  // Last-saved state per action ID
const deviceVariables = ref<any>([])
const showKeyboard = ref<any>(false)
/**
 * Since there is a little delay before playing,
 * temporarily disable insert menu.
 */
const shouldDisableInsertMenu = ref(false)

const deviceScreen = ref<InstanceType<typeof RunDeviceScreen>>()

const store = useStore()
const {showError, runLocked, runLockedMatcher} = store
const undoRedoStore = useUndoRedoStore()

const {openDisconnectTimeout, closeDisconnectTimeout} = useAutoDisconnectStore()

onBeforeMount(async () => {
    VueCookieNext.setCookie("sticky-device", "true")
    testCaseId.value = parseInt(route.params.testId as string)

    await loadPage();
    await initializeDevice()
})

onMounted(async () => {
    document.addEventListener("keydown", processKeyboardInput)
    if (route.params.vars) {
        viewVariables.value = JSON.parse(route.params.vars as string)
    }
})

onBeforeUnmount(() => {
    VueCookieNext.removeCookie("sticky-device")
    document.removeEventListener("keydown", processKeyboardInput)
    closeDisconnectTimeout()
    undoRedoStore.clear()
})

const hasActions = computed(() => {
    return !!testCaseService.value.getActions().length
})

const currentActionObj = computed(() => {
    return testCaseService.value.getActionById(device.value.deviceStatus.currentAction)
})

const showUseVariable = computed(() => {
    return (!!currentActionObj.value && getActionInputType(currentActionObj.value) == "EVAL")
})

const currentActionFrom = computed(() => {
    return device.value.deviceStatus.currentAction &&
      device.value.deviceStatus.currentAction > END_ACTION_ID ? device.value.deviceStatus.currentAction : null
})

const canPlayCurrentAction = computed(() => {
    return (
        !device.value.deviceStatus.playing &&
        !!currentActionObj.value &&
        (!!device.value.deviceStatus.nextFrame || currentActionObj.value.type == "TEXT")
    )
})

const actionsIds = computed(() => {
    return program.value.actions.map((a) => a.id)
})

const isEnd = computed(() => device.value.deviceStatus.currentAction === -1)

watch(
    () => device.value.deviceStatus.currentAction,
    (newValue) => {
        if (justInserted.value && justInserted.value != newValue) {
            if (program.value.mode == "edit") {
                // edit mode was temporary to add details to the inserted action rather than autoplay
                // now switching back
                setMode("insert", true)
            }
            justInserted.value = null
        } else if (newValue == END_ACTION_ID) {
            if (program.value.mode == "edit") {
                // no edit mode at the end of program
                setMode("insert", true)
            }
        }
    }
)

watch(
    () => device.value.deviceStatus.playing,
    (newValue) => {
        if (!newValue) {
            device.value.setPlayingTo(null)
            if (!document.hasFocus()) {
                const audio = new Audio(bingUrl)
                audio.play().catch()
            }
        }
    }
)

watch(
    () => device.value.deviceId,
    async (newValue) =>  {
        if (newValue) {
            await loadDeviceVariables()
        }
    }
)

async function initializeDevice() {
    const recordingId = parseInt(route.query.useRecordingData as string)
    const imageUrl = route.query.imageUrl
    const playTo = parseInt(route.query.playTo as string) || null

    if (recordingId) {
        if (playTo) router.replace({ query: { useRecordingData: recordingId } })
        try {
            let recording = await getRecording(recordingId)
            device.value = new DummyDeviceService(recording, imageUrl?.toString())
            viewVariables.value = recording.meta?.variables ?? null
            device.value.deviceStatus.isDummy = false
            if (playTo) {
                const action = testCaseService.value.getActionById(playTo)
                device.value.activateAction(action)
            }
        }
        catch (e: any) {
            if (e.response?.status == 404) {
                throw new Error(`Sorry, recording with id ${recordingId} has been removed`)
            }
            throw(e)
        }
    }

    if (imageUrl) {
        device.value.deviceStatus.imageUrl = imageUrl as string
    } else {
        device.value = new RealDeviceService()
        device.value.errorHandler = showError
        device.value.testCaseId = testCaseId.value
        device.value.configId = testCaseService.value.getConfig().id
        let existingDeviceId = sessionStorage.getItem(`device-${device.value.configId}`);

        if (existingDeviceId && !playTo) {
            console.log(`found device id: ${existingDeviceId}`)
            device.value.deviceId = existingDeviceId;
        }

        await device.value.connectDevice(viewVariables.value);

        if (!existingDeviceId) {
            const firstAction = testCaseService.value.firstAction()
            await device.value.activateAction(firstAction ?? { id: END_ACTION_ID } as ActionDTO)
        }
    }

    if (playTo) {
        await device.value.playFromStartTo(playTo);
    }

    const failedActionsObj = await getFailedActions(testCaseId.value)
    failedActions.value = failedActionsObj.map((actionId: number) => ({actionId}))

    openDisconnectTimeout(async () => {
        await closeSession(null, false)
    })
}

async function loadDeviceVariables() {
    deviceVariables.value = await getDeviceVariables(device.value.deviceId)
    for (let group of deviceVariables.value) {
        for (let variable of group.variables) {
            if (variable.name == "BRANCH" && !variable.isOverridden) {
                program.value.branch = variable.value
                console.log(variable)
            }
        }
    }
}

async function loadPage() {
    await testCaseService.value.loadTestCase(testCaseId.value!!)
    project.value = testCaseService.value.getTestCase().project
    program.value.actions = testCaseService.value.getActions()
    program.value.actionsLoaded = true
    document.title = testCaseService.value.getTestCase().name
    // Populate snapshots for all loaded actions
    actionSnapshots.clear()
    for (const a of program.value.actions) {
        actionSnapshots.set(a.id, cloneForUndo(a))
    }
    if (program.value.actions.length == 0 && !testCaseService.value.predecessor) {
        setMode("insert")
    }
}

async function processKeyboardInput(event: KeyboardEvent) {
    if (event.ctrlKey || event.metaKey) {
        if (event.key === "z" && !event.shiftKey) {
            event.preventDefault()
            if (!device.value.deviceStatus.playing) runLocked(performUndo)
            return
        }
        if (event.key === "y" || (event.key === "z" && event.shiftKey) || event.key === "Z") {
            event.preventDefault()
            if (!device.value.deviceStatus.playing) runLocked(performRedo)
            return
        }
    }
    if (!(program.value.actionsLoaded && hasActions.value) || event.metaKey || event.ctrlKey || showKeyboard.value) return
    let key = event.key
    if (program.value.mode.startsWith("insert")) {
        const specialKeys: any = {
            Enter: "ENTER",
            Tab: "TAB",
            " ": "SPACE",
            PageDown: "PAGE_DOWN",
            PageUp: "PAGE_UP",
            Backspace: "BACK_SPACE",
            ArrowUp: "UP",
            ArrowRight: "RIGHT",
            ArrowDown: "DOWN",
            ArrowLeft: "LEFT",
            Home: "HOME",
            End: "END",
        }
        const alphanumRegexp =
            /^[A-Za-z0-9\u00AA\u00B5\u00BA\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u052F\u0531-\u0556\u0559\u0561-\u0587\u05D0-\u05EA\u05F0-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u08A0-\u08B4\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0980\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0AF9\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D\u0C58-\u0C5A\u0C60\u0C61\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D5F-\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E87\u0E88\u0E8A\u0E8D\u0E94-\u0E97\u0E99-\u0E9F\u0EA1-\u0EA3\u0EA5\u0EA7\u0EAA\u0EAB\u0EAD-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16F1-\u16F8\u1700-\u170C\u170E-\u1711\u1720-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1877\u1880-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191E\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4B\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1CE9-\u1CEC\u1CEE-\u1CF1\u1CF5\u1CF6\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312D\u3131-\u318E\u31A0-\u31BA\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FD5\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA69D\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA7AD\uA7B0-\uA7B7\uA7F7-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA8FD\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uA9E0-\uA9E4\uA9E6-\uA9EF\uA9FA-\uA9FE\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA7E-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB65\uAB70-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]$/
        let type = ""
        let text = ""

        if (key in specialKeys) {
            event.preventDefault()
            type = "KEY"
            text = specialKeys[key]
        } else if (alphanumRegexp.test(key)) {
            type = "ENTER"
            text = key
        }
        if (type) {
            setMode("edit")
            await createTextAction(type, text)
            if (type != "ENTER") {
                await playCurrentAction()
            }
        }
    }
}

async function createKeyAction(keyType: any) {
    openKeyboard(false)
    let type = ""
    let keyTypeStr = ""
    if (typeof keyType == 'string') {
        type = "KEY"
        keyTypeStr = keyType
    }
    else {
        type = "KEY_SEQUENCE"
        keyTypeStr = JSON.stringify(keyType)
    }
    await createTextAction(type, keyTypeStr)
    await playCurrentAction()
}

function cloneForUndo(action: any): ActionDTO {
    return JSON.parse(JSON.stringify(toRaw(action)))
}

function sanitizeForSave(action: any): any {
    return {
        ...action,
        timeout: action.timeout ?? 0,
        disabled: action.disabled ?? false,
        branches: action.branches ?? "",
        inputs: action.inputs ?? [],
        type: action.type ?? "POINTER"
    }
}

function captureActionState(action: any): ActionDTO {
    return cloneForUndo(action)
}

async function createTextAction(type: string, textValue: string) {
    const text = textValue ? textValue : (type == "SCROLL" ? "1 down" : "")
    const prevCurrent = device.value.deviceStatus.currentAction
    undoRedoStore.isUndoRedoing = true
    const action = await doCreateAction("TEXT")
    action.inputs.push({type, text} as ActionInput)
    await doSaveAction(action)
    undoRedoStore.isUndoRedoing = false
    const parentService = testCaseService.value.getDirectParent(action.id) ?? testCaseService.value
    const cloned = cloneForUndo(action)
    console.log("[UNDO/REDO] createTextAction - storing undo entry, action inputs:", JSON.stringify(cloned.inputs), "matcherId:", cloned.matcherId)
    undoRedoStore.pushUndo({
        type: "CREATE", description: `Create ${type} action`, timestamp: Date.now(),
        data: { type: "CREATE", createdAction: cloned, parentTestCaseId: parentService.getTestCase().id, insertIndex: parentService.actions.findIndex((a: any) => a.id === action.id), previousCurrentAction: prevCurrent }
    })
    setMode("edit")
    await focusActionInput(action)
}

async function focusActionInput(action: any) {
    if (program.value.mode != "edit") return
    action.newAction = true
    await nextTick()
    let inputElem = document.querySelectorAll(".newAction input, .newAction textarea")[0]
    if (inputElem) (inputElem as HTMLInputElement).focus()
    action.newAction = false
    await nextTick()
    delete action.newAction
}

async function setInteraction(interaction: string) {
    const a = currentActionObj.value
    if (!a) return
    const prevState = captureActionState(a)
    if (interaction != "OCR")
        a.inputs = []
    a.type = ActionDTO.type.POINTER
    switch (interaction) {
        case "CHECK":
            break
        case "CLICK":
            a.inputs.push({type: "CLICK", x: 0.5, y: 0.5} as ActionInput)
            break
        case "DOUBLE_CLICK":
            a.inputs.push({type: "DOUBLE_CLICK", x: 0.5, y: 0.5} as ActionInput)
            break
        case "RIGHT_CLICK":
            a.inputs.push({type: "RIGHT_CLICK", x: 0.5, y: 0.5} as ActionInput)
            break
        case "HOVER":
            a.inputs.push({type: "HOVER", x: 0.5, y: 0.5} as ActionInput)
            break
        case "SWIPE_RIGHT":
            a.inputs.push({type: "CLICK", x: 0, y: 0.5} as ActionInput)
            a.inputs.push({type: "SWIPE", x: 1, y: 0.5} as ActionInput)
            break
        case "SWIPE_DOWN":
            a.inputs.push({type: "CLICK", x: 0.5, y: 0} as ActionInput)
            a.inputs.push({type: "SWIPE", x: 0.5, y: 1} as ActionInput)
            break
        case "FIND_IMAGE":
            a.inputs.push({type: "FIND_IMAGE"} as ActionInput)
            a.type = "CONTROL"
            break
        case "OCR":
            if (a.inputs[1] && a.inputs[1].type == "SWIPE") {
                a.inputs[0].type = "OCR"
                a.inputs[1].type = "OCR"
            }
            else {
                a.inputs = []
                a.inputs.push({ type: "OCR", x: 0.5, y: 0.5 } as ActionInput)
                a.inputs.push({ type: "OCR", x: 1.5, y: 1.5 } as ActionInput)
            }
            break
    }
    await doSaveAction(a, false, prevState)
}

async function connectDevice() {
    device.value.configId = testCaseService.value.getConfig().id
    await device.value.connectDevice()
}

async function closeSession(status: any, redirect = true) {
    try {
        await device.value?.disconnectDevice(status)
    } catch (e) {
        console.error(e)
    }
    if (redirect) await router.replace(`/testcase/${testCaseId.value}`)
}

async function doSaveAction(action: any, noReplay = false, previousState?: ActionDTO) {
    if (!undoRedoStore.isUndoRedoing && action.id) {
        // Use provided previousState, or fall back to last-saved snapshot
        if (!previousState) previousState = actionSnapshots.get(action.id)
        if (previousState) {
            // If the last undo entry is a CREATE for this same action, update its
            // stored action data instead of pushing a separate SAVE entry.
            // This merges create+edit into a single undoable operation.
            const lastEntry = undoRedoStore.undoStack[undoRedoStore.undoStack.length - 1]
            if (lastEntry && lastEntry.data.type === "CREATE" && (lastEntry.data as any).createdAction.id === action.id) {
                (lastEntry.data as any).createdAction = cloneForUndo(action)
            } else {
                undoRedoStore.pushUndo({
                    type: "SAVE", description: "Update action", timestamp: Date.now(),
                    data: { type: "SAVE", previousState, newState: cloneForUndo(action), previousCurrentAction: device.value.deviceStatus.currentAction }
                })
            }
        }
        // Update snapshot to current state (for next save)
        actionSnapshots.set(action.id, cloneForUndo(action))
    }
    testCaseService.value.replaceAction(action)

    const inputType = action.inputs[0]?.type
    if (!action.inputs[0] || ["CLICK", "RIGHT_CLICK", "DOUBLE_CLICK", "SWIPE", "HOVER"].includes(inputType)) {
        action.type = "POINTER"
    }
    action.imageUrl = device.value.deviceStatus.imageUrl
    runLocked(async () => {
        await stopAllActions()
        await saveAction(action).catch(loadPage)
    })
    await store.lock
    await replayIfRequired(action, noReplay)
}

async function autoCreateJumpTag() {
    const a = currentActionObj.value
    const aType = getActionInputType(a)
    if (["JUMP_IF_TRUE", "JUMP_IF_FALSE"].includes(aType)) {
        prevInsert.value = 'insert-default'
        if (
            !!a!.inputs[0] &&
            !!a!.inputs[0].text &&
            !!a!.inputs[0].text.length &&
            !tagExists(a!.inputs[0].text, program.value.actions)
        ) {
            const isLoadTrue = device.value.deviceStatus.clipboard.toLowerCase() != "false"
            if (aType == (isLoadTrue ? "JUMP_IF_FALSE" : "JUMP_IF_TRUE")) {
                const action = await doCreateAction("MARKER", "", "#" + currentActionObj.value!.inputs[0].text, 1, false)
            }
        }
    }
}

async function replayIfRequired(action: any, noReplay: boolean) {
    if (noReplay || action.id == device.value.deviceStatus.currentAction || action.id == justInserted.value) return
    if (doReplayAfterChange(action)) throwRestartRequired()
}

function throwRestartRequired() {
    throw new DeviceError(
        {errorMessage: "You have changed test steps that were already played. It is recommended to replay all actions."},
        "WARNING",
        [{label: "Replay", callback: () => replay()}]
    )
}

function doReplayAfterChange(action: any) {
    return replayAfterChange(action) && !isActionAfterCurrent(action)
}

async function replay() {
    await playToActionFromCurrent(currentActionObj.value || {id: END_ACTION_ID}, true)
}

async function doCreateAction(type: string, inputType = "", inputText = "", pos = 0, activate = true, isPasted = false) {
    const prevCurrent = device.value.deviceStatus.currentAction
    let aid = device.value.deviceStatus.currentAction!
    const parentService = testCaseService.value.getDirectParent(aid) ?? testCaseService.value
    if (!parentService) return

    if (pos) {
        let aIndex = parentService.actions.findIndex(a => a.id == aid)
        aid = parentService.actions[aIndex + pos]?.id || END_ACTION_ID
    }

    let aIndex = parentService.actions.findIndex(a => a.id == aid)
    const imageUrl = device.value.deviceStatus.imageUrl
    let action = await createAction(parentService.getTestCase().id, aid!, type, imageUrl!)
    action.branches = program.value.branch
    action = reactive(action)
    if (aIndex >= 0) {
        parentService.actions.splice(aIndex, 0, action)
    } else {
        parentService.actions.push(action)
    }
    actionSnapshots.set(action.id, cloneForUndo(action))
    if (action.type == "MARKER") {
        action.inputs.push({type: "TAG", text: inputText} as ActionInput)
        await doSaveAction(action)
    } else if (action.type == "PROMPT") {
        action.inputs.push({type: "PROMPT", text: inputText} as ActionInput)
        await doSaveAction(action, true)
    } else if (action.type == "CONTROL") {
        if (!isPasted) {
            action.inputs.push({type: inputType, text: inputText} as ActionInput)
            await doSaveAction(action, true)
        }
    }
    if (inputType === "FIND_IMAGE") {
        device.value.deviceStatus.findImage = true
        retakeAction()
    }
    if (action.type != "POINTER") {
        device.value.deviceStatus.nextFrame = null
    }
    if (activate) {
        await device.value.activateAction(action)
        setMode("edit")
        await focusActionInput(action)
        justInserted.value = action.id
    }
    if (!undoRedoStore.isUndoRedoing) {
        const ps = testCaseService.value.getDirectParent(action.id) ?? testCaseService.value
        undoRedoStore.pushUndo({
            type: "CREATE", description: `Create ${type} action`, timestamp: Date.now(),
            data: { type: "CREATE", createdAction: cloneForUndo(action), parentTestCaseId: ps.getTestCase().id, insertIndex: ps.actions.findIndex((a: any) => a.id === action.id), previousCurrentAction: prevCurrent }
        })
    }
    return action
}

async function createControlAction(type: string) {
    let text = undefined
    if (type == "EVAL:EQUALS") {
        text = '$CLIPBOARD == \"' + device.value.deviceStatus.clipboard + '\"'
    } else if (type == "EVAL:ERROR") {
        text = 'error("Fail here")'
    } else if (type == "EVAL:MICROTIME") {
        text = 'microtime()'
    }
    type = type.replace(/:.*/,'')
    await doCreateAction("CONTROL", type, text)
}

async function setMatcher(matcher: any, clickCoordinates: any, callback: CallableFunction) {
    runLockedMatcher(async () => {
        await blockScreen(async () => {
            let action = testCaseService.value.getActionById(device.value.deviceStatus.currentAction)
            let isInserted = false
            if (program.value.mode.startsWith("insert")) {
                action = await doCreateAction("POINTER")
                isInserted = true
            } else {
                if (!action) return
            }
            if (clickCoordinates) {
                matcher = await device.value.setMatcherFromClick(
                    {
                        deviceId: device.value.deviceId,
                        y: clickCoordinates.y,
                        x: clickCoordinates.x,
                        imageUrl: device.value.deviceStatus.imageUrl
                    },
                    action.id
                )
            } else if (matcher) {
                matcher.deviceId = device.value.deviceId
                matcher = await device.value.setMatcher(matcher, action.id)
            }

            callback(action, matcher)
            await bindMatcherToAction(action, matcher)
            if (isInserted && action.inputs.length && action.inputs[0].type != 'FIND_IMAGE') await playAction(action.id)
        })
    })
}

async function bindMatcherToAction(action: any, matcher: any) {
    action.matcherId = matcher?.data.id
    action.imageUrl = device.value.deviceStatus.imageUrl
    await doSaveAction(action, true)
    device.value.deviceStatus.retakeAnchorArea = false
}

async function doDeleteAction(a: ActionDTO, debranch: boolean=false) {
    if (debranch) {
        const prevState = captureActionState(a)
        if (program.value.branch.includes("!")) {
            a.branches += "," + program.value.branch
        } else {
            a.branches += "!" + program.value.branch
        }
        doSaveAction(a, false, prevState)
        return
    }
    const prevCurrent = device.value.deviceStatus.currentAction
    const parentService = testCaseService.value.getDirectParent(a.id) ?? testCaseService.value
    const insertIndex = parentService.actions.findIndex((act: any) => act.id === a.id)
    const deletedClone = cloneForUndo(a)

    if (a.id == device.value.deviceStatus.currentAction) {
        await deleteAction(a)
        const action = testCaseService.value.removeAction(a.id)
        await device.value.activateAction(action ? action : {id: END_ACTION_ID})
    } else {
        let replayStatus = doReplayAfterChange(a)
        await deleteAction(a)
        testCaseService.value.removeAction(a.id)
        if (replayStatus) throwRestartRequired()
    }
    if (!undoRedoStore.isUndoRedoing) {
        undoRedoStore.pushUndo({
            type: "DELETE", description: "Delete action", timestamp: Date.now(),
            data: { type: "DELETE", deletedAction: deletedClone, parentTestCaseId: parentService.getTestCase().id, insertIndex, previousCurrentAction: prevCurrent }
        })
    }
}

async function cutAction(a: any) {
    const prevCurrent = device.value.deviceStatus.currentAction
    const parentService = testCaseService.value.getDirectParent(a.id) ?? testCaseService.value
    const insertIndex = parentService.actions.findIndex((act: any) => act.id === a.id)
    const deletedClone = cloneForUndo(a)

    cutClipboards.value.push(a)
    undoRedoStore.isUndoRedoing = true
    await doDeleteAction(a)
    undoRedoStore.isUndoRedoing = false

    undoRedoStore.pushUndo({
        type: "DELETE", description: "Cut action", timestamp: Date.now(),
        data: { type: "DELETE", deletedAction: deletedClone, parentTestCaseId: parentService.getTestCase().id, insertIndex, previousCurrentAction: prevCurrent }
    })
}

async function doPasteAction() {
    const prevCurrent = device.value.deviceStatus.currentAction
    const cutActions = cutClipboards.value.slice(0)
    const subEntries: UndoEntry[] = []

    undoRedoStore.isUndoRedoing = true
    for (let cutAction of cutActions) {
        const newAction = await doCreateAction(cutAction.type, "", "", 0, false, true)
        const src = cloneAction(cutAction)
        Object.assign(newAction, {
            type: src.type,
            inputs: src.inputs,
            disabled: src.disabled,
            timeout: src.timeout,
            matcherId: src.matcherId,
        })
        await doSaveAction(newAction, true)
        const ps = testCaseService.value.getDirectParent(newAction.id) ?? testCaseService.value
        subEntries.push({
            type: "CREATE", description: "Paste action", timestamp: Date.now(),
            data: { type: "CREATE", createdAction: cloneForUndo(newAction), parentTestCaseId: ps.getTestCase().id, insertIndex: ps.actions.findIndex((a: any) => a.id === newAction.id), previousCurrentAction: prevCurrent }
        })
    }
    undoRedoStore.isUndoRedoing = false

    undoRedoStore.pushUndo({
        type: "COMPOUND", description: `Paste ${cutActions.length} action(s)`, timestamp: Date.now(),
        data: { type: "COMPOUND", entries: subEntries, previousCurrentAction: prevCurrent }
    })
}

function cloneAction(cutAction: any) {
    const rawAction = toRaw(cutAction)
    try {
        return structuredClone(rawAction)
    } catch (e) {
        console.error('Failed to clone an object', cutAction, e)
        return JSON.parse(JSON.stringify(rawAction))
    }
}

async function retakeAction() {
    await stopAllActions()
    setMode("edit")
    device.value.deviceStatus.retakeAnchorArea = true
}

async function playAllActions(from?: number, to?: number, n?: number, forceReset= false) {
    if (to == END_ACTION_ID) to = undefined
    let reset = true
    if (!forceReset) {
        const result = await device.value.play(from, to, n)
        reset = result.status == 'RESTART_REQUIRED'
    }
    if (reset && !n) {
        await loadPage()
        await resetDevice()
        await device.value.playFromStartTo(to)
    }
}

async function playAllActionsFromCurrent() {
    await playAllActions(currentActionFrom.value!)
}

async function playToActionFromCurrent(toAction: any, forceReset = false) {
    await playAllActions(currentActionFrom.value ?? END_ACTION_ID, toAction?.id, undefined, forceReset)
}

function isActionAfterCurrent(playToAction: any) {
    const currentActionId = device.value.deviceStatus.currentAction;
    const playToActionId = playToAction.id;

    if (currentActionId == END_ACTION_ID) {
        return false
    } else {
        if (playToActionId === END_ACTION_ID) {
            return true
        } else {
            const playToActionIndex = testCaseService.value.getIndex(playToActionId, true);
            const currentActionIndex = testCaseService.value.getIndex(currentActionId, true)
            return playToActionIndex > currentActionIndex
        }
    }
}

async function playCurrentAction() {
    if (justInserted.value) {
        await autoCreateJumpTag()
    }
    if (device.value.deviceStatus.currentAction)
        await playAction(device.value.deviceStatus.currentAction)
}

async function playAction(actionId: number) {
    shouldDisableInsertMenu.value = true
    try {
        await store.lock
        await playAllActions(actionId, undefined, 1)
    } finally {
        shouldDisableInsertMenu.value = false
    }
}

async function resetDevice() {
    await loadPage()
    if (! await device.value.resetDevice())
        await initializeDevice()
    await device.value.activateAction(testCaseService.value.firstAction() ?? {id: END_ACTION_ID} as ActionDTO)
}

async function jumpAction(action: any) {
    await stopAllActions()
    await device.value.activateAction(action)
}

async function splitTestCase(action: ActionDTO) {
    await sendSplitTestCaseRequest(testCaseId.value!, action.id)
    location.reload()
}

async function inlineTestCase() {
    await sendInlineTestCaseRequest(testCaseId.value!)
    location.reload()
}

async function stopAllActions() {
    await device.value.stopAllActions()
}

async function setMode(mode: string, noAutoplay = false) {
    if (mode == "insert") {
        mode = prevInsert.value || "insert-default"
    }
    if (mode.startsWith("insert")) {
        if (!noAutoplay && justInserted.value && currentActionObj.value) {
            await playCurrentAction()
        }
        prevInsert.value = mode
    }
    program.value.mode = mode
}

async function showGettingStartedVideo() {
    openGettingStarted.value = true
    await nextTick()
    openGettingStarted.value = false
}

function openKeyboard(value: boolean) {
    showKeyboard.value = value
}


async function openTestCaseInEditor(data: any) {
    await closeSession(null)
    router.push(`/run/${data.testCaseId}`)
}

function updateDevice(value: any) {
    device.value.deviceStatus = {
        ...device.value.deviceStatus,
        ...value,
    }
}

function addVariable(data: any) {
    vEditAndView.value!.addVariable(data)
}

function syncAqua(message: any) {
    vEditAndView.value!.syncAqua(message)
}

// Chatbot integration - expose methods for AI recorder
async function getScreenshotBase64(): Promise<{ base64: string, width: number, height: number }> {
    const imageUrl = device.value.deviceStatus.imageUrl
    if (!imageUrl) {
        throw new Error("No screenshot available")
    }

    // Load image to get dimensions
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = reject
        img.src = imageUrl
    })

    const width = img.naturalWidth
    const height = img.naturalHeight

    // Fetch and convert to base64
    const response = await fetch(imageUrl)
    const blob = await response.blob()
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const base64 = (reader.result as string).split(",")[1]
            resolve({ base64, width, height })
        }
        reader.onerror = reject
        reader.readAsDataURL(blob)
    })
}

// Create a POINTER action with anchor area from click coordinates
// x, y are PIXEL coordinates on the screen (from Claude's computer-use tool)
async function createPointerActionWithAnchor(x: number, y: number, inputType: string): Promise<void> {
    // x, y are already absolute pixel coordinates from Claude's computer-use tool
    const absX = x
    const absY = y

    console.log(`[AI Recorder] Creating click at pixel coordinates (${absX}, ${absY})`)

    // Must be in insert mode for setMatcher to create a new action
    if (!program.value.mode.startsWith("insert")) {
        await setMode("insert-default", true)
    }

    return new Promise((resolve) => {
        setMatcher(null, { x: absX, y: absY }, (action: any, matcher: any) => {
            // Calculate click position relative to the anchor/matcher area
            console.log(`[AI Recorder] Matcher object:`, JSON.stringify(matcher))

            const frameDim = matcher.data
            console.log(`[AI Recorder] frameDim:`, JSON.stringify(frameDim))

            let relX = (absX - frameDim.x0) / (frameDim.x1 - frameDim.x0)
            let relY = (absY - frameDim.y0) / (frameDim.y1 - frameDim.y0)

            console.log(`[AI Recorder] Anchor area: (${frameDim.x0}, ${frameDim.y0}) to (${frameDim.x1}, ${frameDim.y1}), click relative to anchor: (${relX}, ${relY})`)

            // If click falls outside anchor bounds, clamp to valid range
            // This can happen when the anchor detection algorithm finds a region
            // that doesn't fully encompass the intended click point
            if (relX < 0 || relX > 1 || relY < 0 || relY > 1) {
                console.warn(`[AI Recorder] Click outside anchor bounds, clamping from (${relX}, ${relY})`)
                relX = Math.max(0, Math.min(1, relX))
                relY = Math.max(0, Math.min(1, relY))
                console.log(`[AI Recorder] Clamped to (${relX}, ${relY})`)
            }

            // Match the exact structure used by manual clicks: {x, y, type}
            action.inputs = [{ x: relX, y: relY, type: inputType }]
            // Signal to continue and play the action
            setTimeout(resolve, 100)
            return true
        })
    })
}

// --- Undo/Redo execution ---

function findParentServiceByTestCaseId(tcId: number): any {
    let svc: any = testCaseService.value
    while (svc) {
        if (svc.getTestCase().id === tcId) return svc
        svc = svc.predecessor
    }
    return testCaseService.value
}

async function performUndo() {
    const entry = undoRedoStore.popUndo()
    if (!entry) return
    undoRedoStore.isUndoRedoing = true
    try {
        await executeUndo(entry)
        undoRedoStore.pushRedo(entry)
    } catch (e) {
        showError(e)
    } finally {
        undoRedoStore.isUndoRedoing = false
    }
}

async function performRedo() {
    const entry = undoRedoStore.popRedo()
    if (!entry) return
    undoRedoStore.isUndoRedoing = true
    try {
        await executeRedo(entry)
        // Push back to undo (isUndoRedoing is true, so pushUndo is suppressed; push directly)
        undoRedoStore.undoStack.push(entry)
    } catch (e) {
        showError(e)
    } finally {
        undoRedoStore.isUndoRedoing = false
    }
}

async function executeUndo(entry: UndoEntry) {
    const data = entry.data
    console.log("[UNDO/REDO] executeUndo type:", data.type)
    switch (data.type) {
        case "CREATE": {
            console.log("[UNDO/REDO] CREATE undo - stored action inputs:", JSON.stringify(data.createdAction.inputs), "matcherId:", data.createdAction.matcherId)
            const action = testCaseService.value.getActionById(data.createdAction.id)
            if (action) {
                testCaseService.value.removeAction(action.id)
                await deleteAction(action)
            }
            break
        }
        case "SAVE": {
            const prev = reactive(sanitizeForSave(cloneForUndo(data.previousState)))
            testCaseService.value.replaceAction(prev)
            await saveAction(prev)
            break
        }
        case "DELETE": {
            const parentSvc = findParentServiceByTestCaseId(data.parentTestCaseId)
            const old = data.deletedAction
            const beforeId = parentSvc.actions[data.insertIndex]?.id ?? END_ACTION_ID
            const newAction = await createAction(data.parentTestCaseId, beforeId, old.type, old.imageUrl)
            const restored = reactive(sanitizeForSave({ ...cloneForUndo(old), id: newAction.id }))
            await saveAction(restored)
            parentSvc.actions.splice(data.insertIndex, 0, restored)
            data.deletedAction = { ...cloneForUndo(data.deletedAction), id: newAction.id }
            break
        }
        case "COMPOUND": {
            for (let i = data.entries.length - 1; i >= 0; i--) {
                await executeUndo(data.entries[i])
            }
            break
        }
    }
}

async function executeRedo(entry: UndoEntry) {
    const data = entry.data
    console.log("[UNDO/REDO] executeRedo type:", data.type, "entry:", JSON.stringify(entry, null, 2).substring(0, 500))
    switch (data.type) {
        case "CREATE": {
            const parentSvc = findParentServiceByTestCaseId(data.parentTestCaseId)
            const old = data.createdAction
            console.log("[UNDO/REDO] CREATE redo - old action inputs:", JSON.stringify(old.inputs), "matcherId:", old.matcherId, "type:", old.type)
            const beforeId = parentSvc.actions[data.insertIndex]?.id ?? END_ACTION_ID
            const newAction = await createAction(data.parentTestCaseId, beforeId, old.type, old.imageUrl)
            console.log("[UNDO/REDO] CREATE redo - new bare action from server:", JSON.stringify(newAction).substring(0, 300))
            const toSave = sanitizeForSave({ ...cloneForUndo(old), id: newAction.id })
            console.log("[UNDO/REDO] CREATE redo - about to PUT:", JSON.stringify(toSave).substring(0, 500))
            const restored = reactive(toSave)
            try {
                const result = await saveAction(restored)
                console.log("[UNDO/REDO] CREATE redo - PUT result:", JSON.stringify(result).substring(0, 500))
            } catch (e) {
                console.error("[UNDO/REDO] CREATE redo - PUT FAILED:", e)
                throw e
            }
            parentSvc.actions.splice(data.insertIndex, 0, restored)
            data.createdAction = { ...cloneForUndo(data.createdAction), id: newAction.id }
            break
        }
        case "SAVE": {
            const next = reactive(sanitizeForSave(cloneForUndo(data.newState)))
            testCaseService.value.replaceAction(next)
            await saveAction(next)
            break
        }
        case "DELETE": {
            const action = testCaseService.value.getActionById(data.deletedAction.id)
            if (action) {
                testCaseService.value.removeAction(action.id)
                await deleteAction(action)
            }
            break
        }
        case "COMPOUND": {
            for (const sub of data.entries) {
                await executeRedo(sub)
            }
            break
        }
    }
}

provide("runPageMethods", {
    getScreenshotBase64,
    doCreateAction,
    doSaveAction,
    playCurrentAction,
    createPointerActionWithAnchor,
    device
})

async function useVariable(variable: any) {
    const prevState = captureActionState(currentActionObj.value!)
    let text = currentActionObj.value!.inputs[0].text
    const newCode = variable.php ? variable.php : `\${"${variable.name}"}`
    text = text!.replace(/(\$\{"[^"]*"\}|\$[a-zA-Z0-9]+|get\("[^"]*"\)|)(\)?)$/, newCode + "$2")
    currentActionObj.value!.inputs[0].text = text
    await doSaveAction(currentActionObj.value, false, prevState)
}

function doSaveConfig(data: any) {
    runLocked(async () => {
        testCaseService.value.setVariables(data)
        await testCaseService.value.saveConfig()
        await loadDeviceVariables()
    })
}
</script>

<template lang="pug">
.run-page-wrapper
    chatbot-panel(v-if="aiRecorderEnabled")
    v-edit-and-view(
        ref="vEditAndView"
        v-if="testCaseService?.isLoaded"
        :mode="program.mode"
        :device="device.deviceStatus"
        :variables="deviceVariables"
        :viewerMode="false"
        :testcase="testCaseService.getTestCase()"
        :showUseVariable="showUseVariable"
        @save="doSaveConfig"
        @useVariable="useVariable"
    )
        template(#header-layout)
            run-header(
                :device="device.deviceStatus"
                :actions="testCaseService.actions"
                :actionsLoaded="program.actionsLoaded"
                :currentAction="currentActionObj"
                :mode="program.mode"
                :testCaseService="testCaseService"
                :shouldDisableInsertMenu="shouldDisableInsertMenu"
                :showKeyboard="showKeyboard"
                :canUndo="undoRedoStore.canUndo"
                :canRedo="undoRedoStore.canRedo"
                @stop="stopAllActions"
                @undo="runLocked(performUndo)"
                @redo="runLocked(performRedo)"
                @playall="playAllActionsFromCurrent"
                @enterInput="createTextAction"
                @tagAction="doCreateAction"
                @controlAction="createControlAction"
                @pressKey="createKeyAction"
                @setInteraction="setInteraction"
                @replay="replay"
                @reset="resetDevice()"
                @play="playCurrentAction"
                @delete="doDeleteAction"
                @update="doSaveAction"
                @retake="retakeAction"
                @cutAction="cutAction"
                @setMode="setMode"
                @showVideo="showGettingStartedVideo"
                @addVariable="addVariable"
                @syncAqua="syncAqua"
                @openKeyboard="openKeyboard"
            )
                v-action-button(
                    main="Done"
                    :mainIconClass="['fas', 'fa-sign-out-alt']"
                    @main="isEnd ? closeSession(true) : closeSession(null)"
                )
        template(#main-layout)
            .play-test-view
                .device-view
                    .spin-container(v-if="device.deviceStatus.connecting")
                        .spinner
                            .spinner-item
                            .spinner-item
                            .spinner-item
                    div(v-if="device.deviceStatus.connectionFailed")
                        p Connection error
                    div(v-if="device.deviceStatus.disconnectedAfterConnected")
                        p No device connected
                    run-device-screen(
                        v-if="device.deviceStatus.connected"
                        ref="deviceScreen"
                        :device="device.deviceStatus"
                        :mode="program.mode"
                        :currentAction="currentActionObj"
                        :justInserted="justInserted"
                        @setMatcher="setMatcher"
                        @updateDevice="updateDevice"
                        @saveAction="doSaveAction"
                        @play="playCurrentAction"
                        @delete="doDeleteAction"
                    )
        template(#side-layout, v-if="sidePanel")
            .spin-container(v-if="!program.actionsLoaded")
                .spinner
                    .spinner-item
                    .spinner-item
                    .spinner-item
            #action-view-top.action-view
                action-list-main(
                    ref="actionListMain"
                    :mode="program.mode"
                    :testCaseService="testCaseService"
                    :device="device"
                    :deviceStatus="device.deviceStatus"
                    :testcase="testCaseService.getTestCase()"
                    :isRoot="!!$root"
                    :justInserted="justInserted"
                    :currentActionObj="currentActionObj"
                    :failedActions="failedActions"
                    :cutClipboards="cutClipboards"
                    @play="playCurrentAction"
                    @stop="stopAllActions"
                    @update="doSaveAction"
                    @pasteAction="doPasteAction"
                    @delete="doDeleteAction"
                    @retake="retakeAction"
                    @playtohere="playToActionFromCurrent"
                    @jump="jumpAction"
                    @splitTestCase="splitTestCase"
                    @inlineTestCase="inlineTestCase"
                    @setMode="setMode"
                    @addEmptyTextAction="createTextAction('ENTER', '')"
                    @playAll="playAllActionsFromCurrent"
                    @closeSession="closeSession($event)"
                    @openTestCaseInEditor="openTestCaseInEditor"
                )

                action-edit(
                    v-if="program.mode.startsWith('insert')"
                    :mode="program.mode"
                    :device="device.deviceStatus"
                    :action="null"
                    :hasActions="hasActions"
                    :cutClipboards="cutClipboards"
                    @play="playCurrentAction"
                    @stop="stopAllActions"
                    @setMode="setMode"
                    @pasteAction="doPasteAction"
                    @addEmptyTextAction="createTextAction('ENTER', '')"
                )
            run-video-display(:open="openGettingStarted" videoId="getting-started")
        template(#footer-layout)
</template>

<style lang="css" scoped>
.run-page-wrapper {
    display: flex;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
}

.run-page-wrapper :deep(.page) {
    width: 100%;
    flex: 1;
    min-width: 0;
}

.play-test-view {
    overflow: hidden;
    display: grid;
    height: 100%;
}

.device-view {
    overflow: hidden;
    max-height: 100%;
}

.action-view {
    width: 50vw;
    margin-top: 80px;
    max-width: 609px;
}

.run-header {
    grid-column: 1/4;
}

.spin-container {
    display: grid;
    grid-template-columns: repeat(1, auto);
    place-items: center;
    gap: 7rem;
    margin-top: 18em;
}
</style>
